// @core/stores/task/TaskTaskNumberingStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { taskApi } from '@core/api';
import type {
  CreateTaskNumberingSchemeRequest,
  TaskNumberingScheme,
  UpdateTaskNumberingSchemeRequest,
} from '@core/types/task';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class TaskTaskNumberingStore {
  // List state
  items: TaskNumberingScheme[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: TaskNumberingScheme | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, TaskNumberingScheme>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): TaskNumberingScheme | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: TaskNumberingScheme | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create numbering scheme
   */
  async CreateTaskNumberingScheme(data: CreateTaskNumberingSchemeRequest): Promise<TaskNumberingScheme> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.CreateTaskNumberingScheme(data);
      const item = response.scheme;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete numbering scheme
   */
  async DeleteTaskNumberingScheme(scheme_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await taskApi.DeleteTaskNumberingScheme(scheme_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== scheme_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === scheme_id) {
          this.currentItem = null;
        }

        this.removeFromCache(scheme_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get numbering scheme
   */
  async GetTaskNumberingScheme(scheme_id: string): Promise<TaskNumberingScheme> {
    // Check cache first
    const cached = this.cache.get(scheme_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await taskApi.GetTaskNumberingScheme(scheme_id);
      const item = response.scheme;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List numbering schemes
   */
  async ListTaskNumberingSchemes(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await taskApi.ListTaskNumberingSchemes(params);

      runInAction(() => {
        this.items = response.schemes?.schemes ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update numbering scheme
   */
  async UpdateTaskNumberingScheme(scheme_id: string, data: UpdateTaskNumberingSchemeRequest): Promise<TaskNumberingScheme> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.UpdateTaskNumberingScheme(scheme_id, data);
      const item = response.scheme;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const taskTaskNumberingStore = new TaskTaskNumberingStore();
